import {AGENCY_KEYS, useAgencySubbrandsQuery} from "@/api/agencyApi";
import CheckIcon from "@/assets/colorable/check.svg?react";
import DotIcon from "@/assets/colorable/dot.svg?react";
import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import InviteUser from "@/assets/colorable/invite_user.svg?react";
import PenEdit from "@/assets/colorable/pen_edit.svg?react";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import CompanyLogoPlaceholder from "@/assets/light_gradient/company_logo_placeholder.svg";
import PlusIcon from "@/assets/light_gradient/plus.svg?react";
import {useLogout} from "@/hooks/useLogout";
import {useHelpUrl} from "@/hooks/useNavigationHooks";
import {packages} from "@/hooks/usePackages";
import {
  useAgencyId,
  useAgencySeats,
  useSelectedAgency,
  useSelectedCompanyReactive,
  useUserPermission,
} from "@/hooks/useUserHooks";
import HelpProfileIcon from "@/images/help_profile_icon.svg?react";
import InvoiceIcon from "@/images/invoice_icon.svg?react";
import LogoutProfileIcon from "@/images/logout_profile_icon.svg?react";
import UserAvatarPlaceholder from "@/images/navbar_logged_out_profile_icon.svg";
import UserProfileIcon from "@/images/user_profile_icon.svg?react";
import clsxm from "@/lib/clsxm";
import {Can} from "@/permissions";
import {queryClient} from "@/queryClient";
import {AGENCY_ROUTES_WITHOUT_NAVIGATION, INFINITY_THRESHOLD} from "@/store/constants";
import {
  createCustomerModalOpenAtom,
  editCompanyMemberModalAtom,
  isAgencyInviteModalOpenAtom,
  multiAccountInviteModalAtom,
  selectedCompanyIdAtom,
} from "@/store/jotaiAtoms";
import useRegistrationStore from "@/store/registration";
import type {TSingleBrand} from "@/store/types";
import useUserStore from "@/store/user";
import type {AxiosResponse} from "axios";
import {produce, WritableDraft} from "immer";
import {useSetAtom} from "jotai";
import {useCallback, useMemo, type FC} from "react";
import {useTranslation} from "react-i18next";
import {Link, useLocation, useNavigate} from "react-router";
import {useMedia} from "react-use";

const NavbarAgencyProfile: FC = () => {
  const {t} = useTranslation(["navigation", "multi_account"]);
  const navigate = useNavigate();
  const location = useLocation();
  const helpUrl = useHelpUrl();
  const screenHeight = useMedia("(min-height: 868px)");
  const setInviteModal = useSetAtom(multiAccountInviteModalAtom);
  const setAgencyInviteModal = useSetAtom(isAgencyInviteModalOpenAtom);
  const setEditCompanyMemberModal = useSetAtom(editCompanyMemberModalAtom);
  const addCustomer = useSetAtom(createCustomerModalOpenAtom);
  const setSelectedCompanyId = useSetAtom(selectedCompanyIdAtom);
  const user = useUserStore(state => state.user);
  const userPermission = useUserPermission();
  const logout = useLogout();
  const setRegistrationValue = useRegistrationStore(state => state.setValue);
  const seats = useAgencySeats();
  const agencyId = useAgencyId();
  const agency = useSelectedAgency();
  const selectedCompany = useSelectedCompanyReactive();
  const subscritpion = useUserStore(state => state.user.package_subscription);
  const pkg = useMemo(() => packages[subscritpion?.name!], [subscritpion]);
  const {data: subBrands} = useAgencySubbrandsQuery({
    agencyId: Number(agencyId),
  });
  const openUserProfile = useCallback(() => {
    setRegistrationValue("isEmailPrefilled", true);
    setRegistrationValue("email", user.email ?? "");
    setRegistrationValue("firstName", user.first_name ?? "");
    setRegistrationValue("lastName", user.last_name ?? "");
    setEditCompanyMemberModal({
      isVisible: true,
      isRegistering: false,
    });
  }, [setRegistrationValue, setEditCompanyMemberModal]);

  return (
    <div className="group relative flex h-14 w-14 items-center">
      <div
        className={
          "border-neutral-grey-light bg-primary-light-solid relative flex h-10 w-[68px] shrink-0 items-center justify-center rounded-full border " +
          (agency?.picture ? "" : "p-1")
        }>
        <div className="border-primary-medium-solid absolute left-0 size-10 rounded-full border bg-white">
          <img
            className={"h-full w-full rounded-full object-cover"}
            src={(!selectedCompany?.id ? agency?.picture : selectedCompany?.picture320) ?? CompanyLogoPlaceholder}
            alt="Profile"
          />
        </div>
        <RightChevronIcon className="text-neutral-black absolute right-2 size-2.5 rotate-90" />
      </div>
      <div className="divide-neutral-grey-lightest border-neutral-white-dark absolute top-14 -right-7 h-[90vh] min-w-[16rem] origin-top-right scale-0 flex-col divide-y-1 overflow-hidden rounded-md border bg-white opacity-0 drop-shadow-lg transition-all delay-100 duration-200 *:px-3 *:py-4 group-hover:scale-100 group-hover:opacity-100 2xl:h-fit">
        <div className="flex flex-col gap-2 [&]:py-2">
          <div className="flex flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <div className="border-neutral-grey-light-dark size-6 shrink-0 rounded-full border">
                <img
                  src={user.profile_picture ?? UserAvatarPlaceholder}
                  className="h-full w-full rounded-full object-fill"
                />
              </div>
              <span className="bg-linear-(--primary-dark-gradient) bg-clip-text font-semibold text-transparent">
                {user.first_name}
              </span>
            </div>
            <span className="text-neutral-black text-xs text-nowrap">
              {t(
                `multi_account:${(userPermission === "REFLUENCED_COMPANY_ADMIN" ? "ADMIN" : userPermission).toLowerCase()}_permissions`,
              )}
            </span>
          </div>
          <button onClick={openUserProfile} className="text-primary-dark-solid self-start text-xs hover:font-bold">
            {t("navigation:edit_profile")}
          </button>
        </div>
        <div className="text-neutral-grey-dark text-center text-xs [&]:py-1">
          {t(agency?.large_brand ? "multi_account:large_brand" : "multi_account:agency")}
        </div>
        <div
          className={clsxm("flex flex-row items-center justify-between", !selectedCompany?.id && "bg-primary-powder")}>
          <div className="flex flex-row items-center gap-2">
            <img
              src={agency?.picture320 ?? CompanyLogoPlaceholder}
              className={clsxm(
                "border-neutral-grey-lightest size-10 self-start rounded-full border",
                !screenHeight && "size-8",
              )}
            />
            <div className="flex flex-col gap-1">
              <span className="font-medium">{agency?.name}</span>
              {pkg && (
                <div className="flex flex-row items-center gap-1">
                  <pkg.icon className="size-3" />
                  <span className="bg-linear-(--primary-dark-gradient) bg-clip-text text-xs text-transparent">
                    {t(`common:${pkg.translationKey}`)}
                  </span>
                  <DotIcon className="text-neutral-grey-dark size-1" />
                  {subscritpion?.instagram_campaigns && <InstagramIcon className="text-neutral-grey-dark size-3" />}
                  {subscritpion?.tiktok_campaigns && <TiktokIcon className="text-neutral-grey-dark size-3" />}
                </div>
              )}
              <div className={clsxm("mt-1 flex flex-col gap-2", screenHeight ? "flex-col gap-2" : "flex-row gap-4")}>
                <button
                  onClick={() => setAgencyInviteModal(true)}
                  className="text-primary-dark-solid flex flex-row items-center gap-1 text-xs font-normal transition-all hover:font-semibold">
                  <InviteUser className="size-3" />
                  {screenHeight && t("invite")}
                </button>
                <button
                  onClick={() => navigate(`/agency/${agencyId}/edit/`)}
                  className="text-primary-dark-solid flex flex-row items-center gap-1 text-xs font-normal no-underline transition-all hover:font-semibold">
                  <PenEdit className="size-3" />
                  {screenHeight && t("navigation:edit_agency")}
                </button>
              </div>
            </div>
          </div>
          {!selectedCompany?.id ? (
            <CheckIcon className="text-primary-medium-solid self-center" />
          ) : (
            <button
              onClick={() => {
                if (AGENCY_ROUTES_WITHOUT_NAVIGATION.includes(location.pathname)) {
                  setSelectedCompanyId(null);
                } else {
                  navigate(`/agency/${agency?.id}/dashboard/`);
                }
              }}
              className="self-center">
              <RightArrowIcon className="border-primary-light-solid text-neutral-grey-light hover:text-primary-dark-solid hover:border-primary-dark-solid size-8 rounded-[4px] border p-2.5 transition-all duration-150" />
            </button>
          )}
        </div>
        <div className="text-neutral-grey-dark text-center text-xs [&]:py-1">
          {agency?.large_brand ? t("navigation:companies") : t("navigation:customers")}
        </div>
        <div className="divide-neutral-grey-lightest flex max-h-[calc(100vh-590px)] flex-col divide-y-1 overflow-auto 2xl:max-h-[calc(100vh-670px)] [&]:px-0 [&]:py-0">
          {subBrands?.map(brand => (
            <div
              key={brand.id}
              className={clsxm(
                "flex shrink flex-row justify-between gap-3 px-3 py-4",
                selectedCompany?.id === brand.id && "bg-primary-powder",
              )}>
              <div className="flex flex-row items-center gap-3">
                <img
                  src={brand.picture320 ?? CompanyLogoPlaceholder}
                  className={clsxm(
                    "border-neutral-grey-lightest size-10 self-start rounded-full border",
                    !screenHeight && "size-8",
                  )}
                />
                <div className={clsxm("flex flex-col", screenHeight ? "gap-3" : "gap-1")}>
                  <span className="font-medium">{brand.company_name}</span>
                  <div className={clsxm("mt-1 flex gap-2", !screenHeight ? "flex-row gap-4" : "flex-col")}>
                    <button
                      onClick={() => {
                        setSelectedCompanyId(brand.id!);
                        setInviteModal({
                          isVisible: true,
                          isViewingOwnCompany: true,
                        });
                      }}
                      className="text-primary-dark-solid flex flex-row items-center gap-1 text-xs font-normal transition-all hover:font-semibold">
                      <InviteUser className="size-3" />
                      {screenHeight && t("invite")}
                    </button>
                    <button
                      onClick={() => navigate(`/agency/${agencyId}/subbrand/${brand.id}/edit/`)}
                      className="text-primary-dark-solid flex flex-row items-center gap-1 text-xs font-normal no-underline transition-all hover:font-semibold">
                      <PenEdit className="size-3" />
                      {screenHeight && t("navigation:edit_customer")}
                    </button>
                  </div>
                </div>
              </div>
              {selectedCompany?.id === brand.id ? (
                <CheckIcon className="text-primary-medium-solid self-center" />
              ) : (
                <button
                  onClick={() => {
                    queryClient.setQueryData<WritableDraft<AxiosResponse<TSingleBrand[]>>>(
                      AGENCY_KEYS.qAgencySubbrands(agencyId!),
                      prevState =>
                        produce(prevState, draft => {
                          if (!draft?.data) return;

                          const idx = draft.data.findIndex(b => b.id === brand.id);

                          if (idx === -1) return;

                          const [itemToMove] = draft.data.splice(idx, 1);
                          draft.data.unshift(itemToMove);
                        }),
                    );

                    if (AGENCY_ROUTES_WITHOUT_NAVIGATION.includes(location.pathname)) {
                      setSelectedCompanyId(brand.id!);
                    } else {
                      navigate(`/agency/${agency?.id}/subbrand/${brand?.id}/dashboard/`);
                    }
                  }}
                  className="self-center">
                  <RightArrowIcon className="border-primary-light-solid text-neutral-grey-light hover:text-primary-dark-solid hover:border-primary-dark-solid size-8 rounded-[4px] border p-2.5 transition-all duration-150" />
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="flex flex-row justify-between text-xs">
          <button onClick={() => addCustomer(true)} className="flex flex-row items-center gap-2">
            <PlusIcon className="size-3.5" />
            <span>{t(agency?.large_brand ? "multi_account:add_company" : "multi_account:add_customer")}</span>
          </button>
          {seats < INFINITY_THRESHOLD && (
            <div className="rounded-full bg-linear-(--primary-light-gradient) p-[2px]">
              <div className="h-full w-full rounded-full bg-white px-2 py-1 text-xs font-semibold">
                <span className="bg-linear-(--primary-light-gradient) bg-clip-text font-bold text-transparent">
                  {t("multi_account:seats_left_short", {count: seats || 0})}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="text-neutral-grey-dark text-center text-xs [&]:py-1">{t("navigation:settings")}</div>
        <div
          className="hover:bg-neutral-white-dark flex h-auto cursor-pointer gap-2 border-b text-xs font-normal"
          onClick={openUserProfile}>
          <UserProfileIcon className="h-4 w-auto shrink-0" />
          {t("navigation:your_profile")}
        </div>
        <Can I="read" a="PaymentHistory">
          <Link
            className="hover:bg-neutral-white-dark flex h-auto cursor-pointer gap-2 border-b text-xs font-normal no-underline"
            to="/invoices">
            <InvoiceIcon className="h-4 w-auto shrink-0" />
            {t("navigation:payment_history")}
          </Link>
        </Can>
        <a className="hover:bg-neutral-white-dark flex cursor-pointer gap-2 border-b no-underline" href={helpUrl}>
          <HelpProfileIcon className="h-4 w-auto shrink-0" />
          <span className="text-xs font-normal">{t("navigation:help")}</span>
        </a>
        <div onClick={logout} className="hover:bg-neutral-white-dark flex cursor-pointer gap-2">
          <LogoutProfileIcon className="h-4 w-auto shrink-0" />
          <span className="text-xs font-normal">{t("navigation:logout")}</span>
        </div>
        <div className="[&]:py-3" />
      </div>
    </div>
  );
};

export default NavbarAgencyProfile;
